import { type RouteRecordRaw } from "vue-router"

import { type Permission } from "@/types"

const routes: Array<RouteRecordRaw> = [
  {
    path: "/adressen",
    name: "AddressPage",
    component: () => import("./address/AddressPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["address:list", "address:unlock"],
    },
    children: [
      {
        path: "gemeinden/:id",
        name: "AddressCommuneDialogEdit",
        component: () => import("@/views/stammdaten/commune/CommuneDialog.vue"),
        props: { readonly: true },
        meta: {
          requiredRoles: <Permission[]>["commune:view", "address:unlock"],
        },
      },
      {
        path: "schulen/:id",
        name: "AddressSchoolDialogEdit",
        component: () => import("@/views/stammdaten/school/SchoolDialog.vue"),
        props: { readonly: true },
        meta: {
          requiredRoles: <Permission[]>["school:view", "address:unlock"],
        },
      },
      {
        path: "jugendaemter/:id",
        name: "AddressYouthWelfareOfficeDialogEdit",
        component: () => import("@/views/stammdaten/ywoffice/YouthWelfareOfficeDialog.vue"),
        props: { readonly: true },
        meta: {
          requiredRoles: <Permission[]>["youth_welfare_office:view", "address:unlock"],
        },
      },
      {
        path: "ansprechpartner/:id",
        name: "AddressContactPersonDialogEdit",
        component: () => import("@/components/admin/ContactSection/ContactDialog.vue"),
        props: {
          readonly: true,
        },
        meta: {
          requiredRoles: <Permission[]>["institution:view", "address:unlock"],
        },
      },
    ],
  },
  {
    path: "/adressen/:id",
    name: "AddressViewEdit",
    component: () => import("./address/AddressView.vue"),
    meta: {
      requiredRoles: <Permission[]>["address:view", "address:unlock"],
    },
    children: [
      {
        path: "",
        name: "AddressFormEdit",
        component: () => import("./address/AddressForm.vue"),
        children: [
          {
            path: "kategorie",
            name: "AddressFormEditCategoryDialog",
            props: { isCreateDialog: true },
            component: () => import("./categories/CategoryDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["category:create", "address:unlock"],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/adressen/erstellen",
    name: "AddressViewCreate",
    component: () => import("./address/AddressView.vue"),
    meta: {
      requiredRoles: <Permission[]>["address:create", "address:unlock"],
    },
    children: [
      {
        path: "",
        name: "AddressFormCreate",
        component: () => import("./address/AddressForm.vue"),
        children: [
          {
            path: "kategorie",
            name: "AddressFormCreateCategoryDialog",
            component: () => import("./categories/CategoryDialog.vue"),
            meta: {
              requiredRoles: <Permission[]>["category:create", "address:unlock"],
            },
          },
        ],
      },
    ],
  },
  {
    path: "/gemeinden",
    name: "CommunePage",
    component: () => import("./commune/CommunePage.vue"),
    meta: {
      requiredRoles: <Permission[]>["commune:list", "commune:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "CommuneDialogEdit",
        component: () => import("./commune/CommuneDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["commune:view", "commune:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "CommuneDialogCreate",
        component: () => import("./commune/CommuneDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["commune:create", "commune:unlock"],
        },
      },
    ],
  },
  {
    path: "/jugendaemter",
    name: "YouthWelfareOfficePage",
    component: () => import("./ywoffice/YouthWelfareOfficePage.vue"),
    meta: {
      requiredRoles: <Permission[]>["youth_welfare_office:list", "youth_welfare_office:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "YouthWelfareOfficeDialogEdit",
        component: () => import("./ywoffice/YouthWelfareOfficeDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["youth_welfare_office:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "YouthWelfareOfficeDialogCreate",
        component: () => import("./ywoffice/YouthWelfareOfficeDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>[
            "youth_welfare_office:create",
            "youth_welfare_office:unlock",
          ],
        },
      },
    ],
  },
  {
    path: "/schulen",
    name: "SchoolPage",
    component: () => import("./school/SchoolPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["school:list", "school:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "SchoolDialogEdit",
        component: () => import("./school/SchoolDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["school:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "SchoolDialogCreate",
        component: () => import("./school/SchoolDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["school:create", "school:unlock"],
        },
      },
    ],
  },
  {
    path: "/zusatzfunktionen-qualifikationen",
    name: "QualificationPage",
    component: () => import("./qualification/QualificationPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["qualification:list", "qualification:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "QualificationDialogEdit",
        component: () => import("./qualification/QualificationDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["qualification:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "QualificationDialogCreate",
        component: () => import("./qualification/QualificationDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["qualification:create", "qualification:unlock"],
        },
      },
    ],
  },
  {
    path: "/essensarten",
    name: "FoodTypePage",
    component: () => import("./foodType/FoodTypePage.vue"),
    meta: {
      requiredRoles: <Permission[]>["food_type:list", "food_type:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "FoodTypeDialogEdit",
        component: () => import("./foodType/FoodTypeDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["food_type:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "FoodTypeDialogCreate",
        component: () => import("./foodType/FoodTypeDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["food_type:create", "food_type:unlock"],
        },
      },
    ],
  },
  {
    path: "/dienstarten",
    name: "ServiceTypePage",
    component: () => import("./serviceType/ServiceTypePage.vue"),
    meta: {
      requiredRoles: <Permission[]>["service_type:list", "service_type:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "ServiceTypeDialogEdit",
        component: () => import("./serviceType/ServiceTypeDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["service_type:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "ServiceTypeDialogCreate",
        component: () => import("./serviceType/ServiceTypeDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["service_type:create", "service_type:unlock"],
        },
      },
    ],
  },
  {
    path: "/kategorien",
    name: "CategoryPage",
    component: () => import("./categories/CategoryPage.vue"),
    meta: {
      requiredRoles: <Permission[]>["category:list", "category:unlock"],
    },
    children: [
      {
        path: ":id",
        name: "CategoryDialogEdit",
        component: () => import("./categories/CategoryDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["category:unlock"],
        },
      },
      {
        path: "erstellen",
        name: "CategoryDialogCreate",
        component: () => import("./categories/CategoryDialog.vue"),
        meta: {
          requiredRoles: <Permission[]>["category:create", "category:unlock"],
        },
      },
    ],
  },
]

export default routes
